/**
 * Centralized query key factory for type-safe React Query keys.
 * Hierarchical pattern enables targeted invalidation (e.g., invalidating
 * `queryKeys.group(id)` invalidates everything nested under that group).
 *
 * Pure data structure — safe to share between web and mobile.
 */

export const queryKeys = {
  all: ['splitfiesta'] as const,

  auth: () => [...queryKeys.all, 'auth'] as const,
  currentUser: () => [...queryKeys.auth(), 'current'] as const,

  // Public app-version gate (mobile force/recommended update).
  appConfig: () => [...queryKeys.all, 'app-config'] as const,

  // Per-user feature flags; cleared with the rest of the cache on logout.
  featureFlags: () => [...queryKeys.all, 'feature-flags'] as const,

  // The caller's held Pro groups (`GET /pro/groups`).
  proGroups: () => [...queryKeys.all, 'pro', 'groups'] as const,

  groups: () => [...queryKeys.all, 'groups'] as const,
  groupList: (filters?: { search?: string; category?: string }) =>
    [...queryKeys.groups(), 'list', filters ?? null] as const,
  group: (id: string) => [...queryKeys.groups(), id] as const,
  groupWithDashboard: (id: string) =>
    [...queryKeys.group(id), 'dashboard'] as const,
  groupMembers: (groupId: string) =>
    [...queryKeys.group(groupId), 'members'] as const,
  groupInvitations: (groupId: string) =>
    [...queryKeys.group(groupId), 'invitations'] as const,
  placeholderClaims: (groupId: string) =>
    [...queryKeys.group(groupId), 'placeholder-claims'] as const,
  recurringRules: (groupId: string) =>
    [...queryKeys.group(groupId), 'recurring-rules'] as const,

  invitations: () => [...queryKeys.all, 'invitations'] as const,
  invitationsByGroup: (groupId: string) =>
    [...queryKeys.invitations(), 'group', groupId] as const,
  invitationStats: (groupId: string) =>
    [...queryKeys.invitations(), 'stats', groupId] as const,
  invitationValidation: (token: string, userId?: string) =>
    [...queryKeys.invitations(), 'validate', token, userId] as const,
  activeInvitation: (invitationId: string) =>
    [...queryKeys.invitations(), invitationId] as const,

  expenses: () => [...queryKeys.all, 'expenses'] as const,
  expensesByGroup: (
    groupId: string,
    filters?: {
      category?: string;
      subcategory?: string;
      startDate?: string;
      endDate?: string;
      search?: string;
      cursor?: string;
    }
  ) => [...queryKeys.expenses(), 'group', groupId, filters ?? null] as const,
  // Prefix shared by the unfiltered row (`…, null`) and every filtered row
  // (`…, {filters}`) — use it to invalidate or update a group's lists cache-wide.
  expensesByGroupAll: (groupId: string) =>
    [...queryKeys.expenses(), 'group', groupId] as const,
  expense: (id: string) => [...queryKeys.expenses(), id] as const,
  expenseComments: (expenseId: string) =>
    [...queryKeys.expense(expenseId), 'comments'] as const,
  expenseReactions: (expenseId: string) =>
    [...queryKeys.expense(expenseId), 'reactions'] as const,
  claimLink: (token: string) =>
    [...queryKeys.all, 'claim-link', token] as const,
  // The bill's own link, minted by its payer. Kept outside expense(id) so the
  // expense refetches after a mint or a stop never touch it.
  expenseClaimLink: (expenseId: string) =>
    [...queryKeys.all, 'expense-claim-link', expenseId] as const,

  exchangeRates: () => [...queryKeys.all, 'exchange-rates'] as const,
  exchangeRate: (from: string, to: string, date: string) =>
    [...queryKeys.exchangeRates(), from, to, date] as const,

  imports: () => [...queryKeys.all, 'imports'] as const,
  importJob: (jobId: string) => [...queryKeys.imports(), jobId] as const,

  balances: () => [...queryKeys.all, 'balances'] as const,
  userBalance: (userId: string, groupId?: string) =>
    [...queryKeys.balances(), 'user', userId, groupId ?? null] as const,
  groupBalances: (groupId: string) =>
    [...queryKeys.balances(), 'group', groupId] as const,
  simplifiedDebts: (groupId: string, filterByUser = false) =>
    [
      ...queryKeys.balances(),
      'simplified-debts',
      groupId,
      filterByUser,
    ] as const,
  userNetBalance: () => [...queryKeys.balances(), 'net'] as const,

  settlements: {
    all: () => [...queryKeys.all, 'settlements'] as const,
    group: (
      groupId: string,
      filters?: { status?: string | undefined; limit?: number }
    ) =>
      [
        ...queryKeys.settlements.all(),
        'group',
        groupId,
        filters ?? null,
      ] as const,
    user: (filters?: { status?: string; page?: number; limit?: number }) =>
      [...queryKeys.settlements.all(), 'user', filters ?? null] as const,
    detail: (id: string) => [...queryKeys.settlements.all(), id] as const,
    summary: (groupId: string) =>
      [...queryKeys.settlements.all(), 'summary', groupId] as const,
  },

  activities: () => [...queryKeys.all, 'activities'] as const,
  /**
   * Broadest activity-related prefix. Matches BOTH the global feed
   * (`[...all, 'activity', 'feed', ...]`) and every group-scoped
   * feed (`[...all, 'activity', 'group', ...]`). Use this when a
   * mutation (comment / reaction add / delete) needs to touch
   * whichever feeds the user might currently see — saves having
   * to track which group the expense belongs to from the mutation
   * site.
   */
  allActivityFeeds: () => [...queryKeys.all, 'activity'] as const,
  /**
   * Prefix that matches every `activityFeed(params)` variant — pass
   * this to `invalidateQueries` / `setQueriesData` when you want to
   * touch the active feed regardless of which params it was loaded
   * with. React Query matches prefix element-by-element, so the
   * specific 4th element (params object or `null`) of the full key
   * blocks invalidation by the no-arg helper.
   */
  activityFeeds: () => [...queryKeys.all, 'activity', 'feed'] as const,
  activityFeed: (params?: Record<string, unknown>) =>
    [...queryKeys.activityFeeds(), params ?? null] as const,
  /**
   * Prefix that matches every `groupActivityFeed(groupId, params)`
   * variant — the group-scoped sibling of `activityFeeds()`. Use for
   * invalidation regardless of which params the feed was loaded with
   * (the full key's trailing params element blocks prefix matching
   * otherwise).
   */
  groupActivityFeeds: (groupId: string) =>
    [...queryKeys.all, 'activity', 'group', groupId] as const,
  groupActivityFeed: (groupId: string, params?: Record<string, unknown>) =>
    [...queryKeys.groupActivityFeeds(groupId), params ?? null] as const,
  activitiesByGroup: (groupId: string, cursor?: string) =>
    [...queryKeys.activities(), 'group', groupId, cursor ?? null] as const,
  activitiesByUser: (userId: string, cursor?: string) =>
    [...queryKeys.activities(), 'user', userId, cursor ?? null] as const,

  notifications: (filters?: {
    page?: number;
    limit?: number;
    unreadOnly?: boolean;
  }) => [...queryKeys.all, 'notifications', filters ?? null] as const,
  notificationsInfinite: (filters?: {
    limit?: number;
    unreadOnly?: boolean;
    type?: string;
    search?: string;
  }) =>
    [...queryKeys.all, 'notifications', 'infinite', filters ?? null] as const,
  notificationsCount: () =>
    [...queryKeys.all, 'notifications', 'count'] as const,
  notificationSettings: () =>
    [...queryKeys.all, 'notifications', 'settings'] as const,
  userNotifications: (userId: string, unreadOnly?: boolean) =>
    [
      ...queryKeys.all,
      'notifications',
      'user',
      userId,
      { unreadOnly: unreadOnly ?? null },
    ] as const,

  analytics: () => [...queryKeys.all, 'analytics'] as const,
  groupAnalytics: (groupId: string, period?: string) =>
    [...queryKeys.analytics(), 'group', groupId, period ?? null] as const,
  userAnalytics: (userId: string, period?: string) =>
    [...queryKeys.analytics(), 'user', userId, period ?? null] as const,

  categories: () => [...queryKeys.all, 'categories'] as const,
  categoryList: () => [...queryKeys.categories(), 'list'] as const,
  suggestedCategories: (context: { title?: string; amount?: number }) =>
    [...queryKeys.categories(), 'suggestions', context] as const,

  friends: () => [...queryKeys.all, 'friends'] as const,
  friendsList: () => [...queryKeys.friends(), 'list'] as const,
  friend: (id: string) => [...queryKeys.friends(), id] as const,

  user: () => [...queryKeys.all, 'user'] as const,
  userProfile: () => [...queryKeys.user(), 'profile'] as const,
  userSettings: () => [...queryKeys.user(), 'settings'] as const,
  userPasskeys: () => [...queryKeys.user(), 'passkeys'] as const,

  offline: () => [...queryKeys.all, 'offline'] as const,
  pendingExpenses: () => [...queryKeys.offline(), 'pending'] as const,
  pendingExpensesByGroup: (groupId: string) =>
    [...queryKeys.pendingExpenses(), 'group', groupId] as const,

  reports: () => [...queryKeys.all, 'reports'] as const,
  groupReports: {
    all: (groupId: string) =>
      [...queryKeys.reports(), 'group', groupId] as const,
    spending: (
      groupId: string,
      filters?: { startDate?: string; endDate?: string }
    ) =>
      [
        ...queryKeys.reports(),
        'group',
        groupId,
        'spending',
        filters ?? null,
      ] as const,
    monthly: (
      groupId: string,
      filters?: { startMonth?: string; endMonth?: string }
    ) =>
      [
        ...queryKeys.reports(),
        'group',
        groupId,
        'monthly',
        filters ?? null,
      ] as const,
    category: (
      groupId: string,
      filters?: { startMonth?: string; endMonth?: string }
    ) =>
      [
        ...queryKeys.reports(),
        'group',
        groupId,
        'category',
        filters ?? null,
      ] as const,
    yearly: (groupId: string, year: string) =>
      [...queryKeys.reports(), 'group', groupId, 'yearly', year] as const,
    trends: (
      groupId: string,
      filters?: { startMonth?: string; endMonth?: string }
    ) =>
      [
        ...queryKeys.reports(),
        'group',
        groupId,
        'trends',
        filters ?? null,
      ] as const,
    members: (
      groupId: string,
      filters?: { startDate?: string; endDate?: string }
    ) =>
      [
        ...queryKeys.reports(),
        'group',
        groupId,
        'members',
        filters ?? null,
      ] as const,
    member: (
      groupId: string,
      memberId: string,
      filters?: { startDate?: string; endDate?: string }
    ) =>
      [
        ...queryKeys.reports(),
        'group',
        groupId,
        'members',
        memberId,
        filters ?? null,
      ] as const,
  },

  admin: {
    all: () => [...queryKeys.all, 'admin'] as const,
    userSearch: (query: string) =>
      [...queryKeys.admin.all(), 'userSearch', query] as const,
  },
} as const;

export type QueryKey = readonly unknown[];
